import { gql, useMutation, useQuery } from '@apollo/client'
import { ClickAwayListener } from '@mui/material'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GetCurrentUserSpeedSettingQuery } from '~/__generated__/graphql'
import { useLogger } from '../../useLogger'
import { useVideoScale } from '../../useVideoScale'
import { PlayerButton } from '../PlayerButton'
import { MenuItem } from './MenuItem'

const Speeds = [
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1.0, label: '1x' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
]

type Props = {
  video: HTMLVideoElement
}

const GET_CURRENT_USER_SPEED_SETTING_QUERY = gql`
  query getCurrentUserSpeedSetting {
    currentUser {
      id
      speedSetting
    }
  }
`

const UPDATE_CURRENT_USER_SPEED_SETTING_MUTATION = gql`
  mutation updateCurrentUserSpeedSetting($speed: String!) {
    updateUser(input: { speedSetting: $speed }) {
      errors
    }
  }
`

export const Speed = ({ video }: Props) => {
  const { data, refetch } = useQuery<GetCurrentUserSpeedSettingQuery>(
    GET_CURRENT_USER_SPEED_SETTING_QUERY,
  )
  const [updateSpeedSetting] = useMutation(UPDATE_CURRENT_USER_SPEED_SETTING_MUTATION)
  const { videoScale } = useVideoScale()
  const { clickSpeedButton, changeSpeed: changeSpeedLog } = useLogger()
  const [showMenu, setShowMenu] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (data?.currentUser?.speedSetting) {
      video.playbackRate = Number(data.currentUser.speedSetting)
    }
  }, [data, video])

  const handleChangeSpeed = useCallback(
    (speed: number) => {
      video.playbackRate = speed
      changeSpeedLog(speed.toString())
      updateSpeedSetting({ variables: { speed: speed.toString() } }).then(() => {
        refetch()
      })
    },
    [video, changeSpeedLog, updateSpeedSetting, refetch],
  )

  return (
    <ClickAwayListener onClickAway={() => setShowMenu(false)}>
      <div style={{ position: 'relative' }}>
        <PlayerButton
          onClick={() => {
            setShowMenu(!showMenu)
            clickSpeedButton()
          }}
        >
          <Image
            src='/player/speed.png'
            alt='Change Speed'
            width={videoScale * 52}
            height={videoScale * 52}
          />
          {videoScale > 0.4 && (
            <span
              style={{
                fontSize: `${videoScale * 24}px`,
                fontWeight: 'bold',
                marginLeft: '8px',
              }}
            >
              {t('Playback speed')}
            </span>
          )}
        </PlayerButton>
        {showMenu && (
          <div
            style={{
              minWidth: '120px',
              position: 'absolute',
              bottom: `${videoScale * 104}px`,
              left: 0,
              backgroundColor: '#EBECED',
              color: 'black',
              borderRadius: '8px',
              padding: '8px 0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {Speeds.map((speed) => (
              <MenuItem
                key={speed.value}
                onClick={() => handleChangeSpeed(speed.value)}
                active={video.playbackRate === speed.value}
                text={speed.label}
              />
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  )
}
